
import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { useStore } from "@/store/useStore";
import {
  calculateDealerPrice,
  calculateGoldPrice,
  calculateSilverPrice,
  GoldPurity,
  SilverPurity,
  Unit,
} from "@/lib/pricing";
import { normalizePurityForDatabase } from "@/lib/formatPurity";
import { useToast } from "@/components/ui/use-toast";

interface UsePriceLockProps {
  dealerId: string;
  dealerSpread?: number;
  initialMetal?: 'gold' | 'silver';
}

export const usePriceLock = ({ dealerId, dealerSpread, initialMetal }: UsePriceLockProps) => {
  const {
    goldPurity,
    silverPurity,
    silverUnit,
    currentGoldPrice: rawGoldPrice,
    currentSilverPrice: rawSilverPrice,
    setGoldPurity,
    setSilverPurity,
    setSilverUnit,
    fetchCurrentPricesData: refreshPrices,
  } = useStore();

  const [metal, setMetal] = useState<'gold' | 'silver'>('gold');
  const [weight, setWeight] = useState<string>('1');
  const [isPurityModalOpen, setIsPurityModalOpen] = useState(false);
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [isUpdatingPrices, setIsUpdatingPrices] = useState(true);
  const [currentSpread, setCurrentSpread] = useState<number | null>(dealerSpread || null);
  const [basePrice, setBasePrice] = useState(0);
  const [dealerPrice, setDealerPrice] = useState(0);
  const { toast } = useToast();

  const fetchCurrentSpread = async () => {
    try {
      const currentMetal = metal === 'gold' ? 'ORO' : 'ARGENTO';
      const currentPurity = metal === 'gold' ? goldPurity : silverPurity;
      
      console.log(`[usePriceLock] Fetching spread for ${currentMetal} ${currentPurity}`);
      
      // Fix: Remove spaces in the purity value for the query
      // This matches how it's stored in the database (e.g., "24KT" instead of "24 KT")
      const normalizedPurity = String(currentPurity).replace(/\s+/g, '');
      
      console.log(`[usePriceLock] Normalized purity for query: "${normalizedPurity}"`);
      
      const { data, error } = await supabase
        .from('vw_active_quotations')
        .select('spread_type, spread_percentage, spread_fixed')
        .eq('dealer_id', dealerId)
        .eq('metal', currentMetal)
        .eq('purity', normalizedPurity)
        .single();
      
      if (error) {
        console.error('[usePriceLock] Error fetching spread:', error);
        console.log('[usePriceLock] Query parameters:', { dealerId, metal: currentMetal, purity: normalizedPurity });
        return dealerSpread;
      }
      
      if (data) {
        console.log('[usePriceLock] Fetched quotation:', data);
        if (data.spread_type === 'PERCENTAGE' && data.spread_percentage !== null) {
          return data.spread_percentage;
        }
      }
      
      return dealerSpread;
    } catch (error) {
      console.error('[usePriceLock] Error in fetchCurrentSpread:', error);
      return dealerSpread;
    }
  };

  const updatePrices = async () => {
    setIsUpdatingPrices(true);
    let currentBasePrice = 0;
    
    if (metal === 'gold' && rawGoldPrice) {
      currentBasePrice = calculateGoldPrice(rawGoldPrice, goldPurity);
      console.log(`[usePriceLock] Calculated gold base price: ${currentBasePrice} for purity ${goldPurity}`);
    } else if (metal === 'silver' && rawSilverPrice) {
      currentBasePrice = calculateSilverPrice(rawSilverPrice, silverPurity, silverUnit);
      console.log(`[usePriceLock] Calculated silver base price: ${currentBasePrice} for purity ${silverPurity}`);
    }
    
    setBasePrice(currentBasePrice);
    
    // Fetch the latest spread each time prices are updated
    const latestSpread = await fetchCurrentSpread();
    setCurrentSpread(latestSpread === undefined ? null : latestSpread);
    console.log(`[usePriceLock] Using spread value: ${latestSpread} for price calculation`);
    
    if (latestSpread !== null && latestSpread !== undefined && currentBasePrice > 0) {
      const calculatedDealerPrice = calculateDealerPrice(currentBasePrice, latestSpread);
      setDealerPrice(calculatedDealerPrice || currentBasePrice);
      console.log(`[usePriceLock] Calculated dealer price with spread ${latestSpread}: ${calculatedDealerPrice}`);
    } else {
      setDealerPrice(currentBasePrice);
      console.log(`[usePriceLock] Using base price as dealer price: ${currentBasePrice}`);
    }
    setIsUpdatingPrices(false);
  };

  const handlePurityChange = (newPurity: string) => {
    console.log(`[usePriceLock] Impostazione della purezza su: ${newPurity}`);

    if (metal === 'gold') {
      if (goldPurity !== newPurity) {
        setGoldPurity(newPurity as GoldPurity);
      }
    } else {
      if (silverPurity !== newPurity) {
        setSilverPurity(newPurity as SilverPurity);
      }
    }
    // Lascia che useEffect gestisca l'aggiornamento del prezzo.
  };

  const handleMetalChange = (newMetal: 'gold' | 'silver') => {
    if (metal === newMetal) return;
    console.log(`[usePriceLock] Cambio del metallo in: ${newMetal}`);
    setMetal(newMetal);
    // Lascia che useEffect gestisca l'aggiornamento per evitare race condition.
  };

  const handleSubmit = () => {
    const weightValue = parseFloat(weight);
    if (isNaN(weightValue) || weightValue < 1 || weightValue > 100000) {
      toast({
        title: "Errore di validazione",
        description: "Il peso deve essere compreso tra 1g e 100kg",
        variant: "destructive",
      });
      return;
    }
    
    setIsRequestModalOpen(true);
  };

  // Effetto per impostare il metallo iniziale
  useEffect(() => {
    if (initialMetal && initialMetal !== metal) {
      console.log(`[usePriceLock] Impostazione metallo iniziale: ${initialMetal}`);
      setMetal(initialMetal);
    }
  }, [initialMetal]);

  // Effetto unificato per gestire tutti gli aggiornamenti dei prezzi, incluso il caricamento iniziale.
  useEffect(() => {
    console.log(`[usePriceLock] useEffect attivato. Aggiornamento prezzi.`);
    updatePrices();
  }, [dealerId, metal, goldPurity, silverPurity, silverUnit, rawGoldPrice, rawSilverPrice]);

  // Funzione per gestire lo switch del metallo dall'esterno (dai pulsanti)
  const switchToMetal = (targetMetal: 'gold' | 'silver') => {
    console.log(`[usePriceLock] Switch del metallo richiesto: ${targetMetal}`);
    handleMetalChange(targetMetal);
  };

  return {
    metal,
    setMetal: handleMetalChange,
    switchToMetal,
    weight,
    setWeight,
    goldPurity,
    silverPurity,
    silverUnit,
    setSilverUnit,
    isPurityModalOpen,
    setIsPurityModalOpen,
    isRequestModalOpen,
    setIsRequestModalOpen,
    isUpdatingPrices,
    basePrice,
    dealerPrice,
    currentSpread,
    handlePurityChange,
    handleSubmit,
    refreshPrices: async () => {
      await refreshPrices();
      await updatePrices();
    },
    calculateEstimatedValue: () => dealerPrice * (parseFloat(weight) || 0)
  };
};
